import { Link } from 'react-router-dom';
import { Page, Section, Stack } from '@/components/layout';
import { DisplayTitle, Eyebrow, Lead, Prose } from '@/components/typography';
import { GoldLine, SacredGeometry } from '@/components/ornaments';
import { CtaButton, Countdown, TicketCta } from '@/components/ui';
import { EVENTS, formatDateRangeLong, formatDateRangeHero, type TourEvent } from '@/features/events';
import styles from './UnderConstruction.module.css';

export interface UnderConstructionProps {
  /** Destino, si la ruta es una ciudad. `null` = el hub. */
  event: TourEvent | null;
}

/**
 * PLACEHOLDER HONESTO de Fase 0.
 *
 * No finge ser la pagina final: dice que esta en construccion y muestra SOLO
 * datos verificados (fechas, horarios, estado de entradas). Lo sustituyen en
 * Fase 1 FORGE-CHARLIE (hub + Buenos Aires), FORGE-DELTA (Bogota + Miami) y
 * FORGE-ECHO (Mexico + Cancun) — ver docs/PAGE_CONTRACT.md §Sustitucion.
 *
 * Existe ademas como banco de pruebas del substrato: si una primitiva se rompe,
 * se ve aqui antes de que ninguna pagina real la use.
 */
export function UnderConstruction({ event }: UnderConstructionProps) {
  const isHub = event === null;

  return (
    <Page
      documentTitle={isHub ? 'La gira' : event.city}
      description={
        isHub
          ? 'Gira 2027 «Nueva Humanidad Galáctica»: cinco destinos por Latinoamérica.'
          : `${event.city} · ${formatDateRangeLong(event)} — ${event.title}.`
      }
      {...(isHub ? {} : { tone: event.heroTone })}
    >
      <Section spacing="default" background="surface" labelledBy="titulo-placeholder">
        <Stack gap="lg" align="start">
          <Eyebrow withSparkles ink="gold">
            {isHub ? 'Gira 2027' : formatDateRangeHero(event)}
          </Eyebrow>

          <DisplayTitle
            id="titulo-placeholder"
            level={1}
            size={isHub ? 'xl' : 'lg'}
            uppercase={isHub}
            {...(isHub ? {} : { scriptLine: event.titleAccent })}
          >
            {isHub ? 'Nueva Humanidad Galáctica' : event.titleMain}
          </DisplayTitle>

          <GoldLine ornament="star" width="sm" />

          <Lead italic>{isHub ? 'Un viaje de transformación global y cósmica' : event.tagline}</Lead>

          <Prose>
            <p>
              <strong>Esta página está en construcción.</strong> El diseño definitivo llega en la fase
              siguiente. Lo que ves aquí abajo sí es definitivo: son los datos verificados de la gira.
            </p>
          </Prose>

          {isHub ? null : <Countdown event={event} label={`Faltan para ${event.city}`} />}

          {isHub ? null : <TicketCta event={event} />}
        </Stack>
      </Section>

      <Section
        spacing="tight"
        background="alt"
        labelledBy="titulo-destinos"
        ornament={<SacredGeometry variant="flower" size={320} className={styles.geometry} />}
      >
        <Stack gap="md">
          <DisplayTitle id="titulo-destinos" level={2} size="sm">
            {isHub ? 'Los cinco destinos' : 'El resto de la gira'}
          </DisplayTitle>
          <ul className={styles.list} role="list">
            {EVENTS.filter((item) => isHub || item.slug !== event.slug).map((item) => (
              <li key={item.slug}>
                <Link className={styles.item} to={item.path}>
                  <span className={styles.itemCity}>
                    <span aria-hidden="true">{item.flag}</span> {item.city}
                  </span>
                  <span className={styles.itemDate}>
                    <time dateTime={item.startDate}>{formatDateRangeLong(item)}</time>
                  </span>
                </Link>
              </li>
            ))}
          </ul>
          {isHub ? null : (
            <CtaButton as="internal" to="/" variant="outline" size="md">
              Volver a la gira
            </CtaButton>
          )}
        </Stack>
      </Section>
    </Page>
  );
}
